import { PackagingBottomSheet } from '@components/BottomSheet/PackagingBottomSheet';
import { ButcherHeader } from '@components/ButcherHeader';
import { OrderNotesModal } from '@components/Modal/OrderProcessing/OrderNotesModal';
import { ProductNotesModal } from '@components/Modal/ProductNotesModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { ServiceTaskProgress } from '@components/OrderProcessing/ServiceTaskProgress';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useToast } from '@contexts/Toast';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { faChevronDown, faFilePen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  errorMessage,
  formRegex,
  OrderCreationMasterData,
  OrderProcessingMasterData,
  ProductProcessingCrumbSteps,
  ProductType,
  responseTexts,
  timers,
  WeightType,
} from '@helpers/Constants';
import { FrontendRoutes, ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  MasterPackage,
  PackagingOption,
  ProductProcessingOrderSpecies,
  ProductProcessingPacking,
  ProductProcessingProduct,
} from '@helpers/Models';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const ProductInProgressDetails: React.FC = () => {
  const location = useLocation();
  const {
    speciesId,
    productName,
    orderNumber,
    editMode,
    productId,
    speciesName,
  } = location.state ?? {};
  const [productDetails, setProductDetails] =
    useState<ProductProcessingOrderSpecies>({
      orderId: 0,
      orderSpeciesId: 0,
      orderNumber: '',
      orderCheckinInches: 0,
      orderCheckinFeet: 0,
      speciesTag: '',
      imageUrl: '',
      notes: {
        orderCheckinNotes: '',
        orderSpeciesNotes: '',
        orderServiceNotes: [],
      },
      products: [],
    });
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [masterPackages, setMasterPackages] = useState<MasterPackage[]>([]);
  const [finalPackage, setFinalPackage] = useState<ProductProcessingPacking[]>(
    [],
  );
  const [percentageError, setPercentageError] = useState<boolean>(false);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [packageIndex, setpackageIndex] = useState<number>();
  const [showProductNotesModal, setShowProductNotesModal] = useState(false);
  const [productNotesModalMode, setProductNotesModalMode] = useState<
    'view' | 'edit'
  >('view');
  const [finalWeight, setFinalWeight] = useState<number | string>('');

  const [activeProductForNotes, setActiveProductForNotes] =
    useState<ProductProcessingProduct | null>(null);

  const [productIndex, setProductIndex] = useState<number>(1);

  useEffect(() => {
    if (speciesId) {
      fetchproductDetails();
      fetchMasterPackages();
    } else {
      showToastMessage(errorMessage.INVALID_REQUEST, 'error');
      setTimeout(() => {
        navigate(FrontendRoutes.PRODUCT_PROCESSING_LIST);
      }, timers.TOAST_AUTO_CLOSE);
    }
  }, [speciesId]);

  const fetchMasterPackages = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      setMasterPackages(response.data.data.masterData.packageType);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const fetchproductDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(
        ApiEndpoints.PRODUCT_PROCESSING_TASK_DETAILS,
        { orderSpeciesId: speciesId },
      );
      setProductDetails(response.data.data[0]);
      const productIndex = response.data.data[0].products.findIndex(
        (product: { orderProductId: number }) =>
          product.orderProductId === productId,
      );

      const selectedProduct = response.data.data[0].products[productIndex];

      setFinalWeight(
        selectedProduct.productFinalQuantity !== 0
          ? selectedProduct.productFinalQuantity
          : selectedProduct.productQuantity,
      );

      let initializedPacking = selectedProduct.packing;

      if (selectedProduct.productStatus < 2) {
        initializedPacking = initializedPacking.map((pack: any) => ({
          ...pack,
          isFinalPacking: 1,
          packingPercentage: pack.packingPercentage ?? 0,
        }));
      }
      setFinalPackage(initializedPacking);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  useEffect(() => {
    setProductIndex(
      productDetails.products.findIndex(
        (product: { orderProductId: number }) =>
          product.orderProductId === productId,
      ),
    );
  }, [productDetails]);

  const handleSelectPackaging = (packId: PackagingOption): void => {
    if (packageIndex === undefined) return;

    const selectedPackage = masterPackages.find(
      (p) => p.id === packId.productPackageTypeId,
    );
    if (!selectedPackage) return;

    const updatedPacking = [...finalPackage];

    const selectedPackageId = selectedPackage.id;
    const currentPackIdInSlot = updatedPacking[packageIndex]?.orderPackingId;

    const alreadyExists = updatedPacking.some(
      (pack) =>
        pack.productPackageTypeLuId === selectedPackageId &&
        pack.isFinalPacking === 1 &&
        pack.orderPackingId !== currentPackIdInSlot,
    );
    if (alreadyExists) {
      showToastMessage(errorMessage.ORDER_DUPLICATE_PACKAGE, 'error');
      return;
    }

    const newPackageSelected = {
      ...selectedPackage,
      orderPackingId: selectedPackage.id,
      productPackageTypeLuId: selectedPackage.id,
      packingPrice: selectedPackage.price,
      packingPercentage: 0,
      isFinalPacking: 1,
    };

    if (packageIndex >= updatedPacking.length) {
      updatedPacking.push(newPackageSelected);
    } else {
      updatedPacking[packageIndex] = newPackageSelected;
    }

    const finalizedPackages = updatedPacking.filter(
      (p) => p.isFinalPacking === 1,
    );

    if (finalizedPackages.length === 2) {
      const finalUpdatedPacking = updatedPacking.map((pack) => {
        if (pack.isFinalPacking === 1) {
          return { ...pack, packingPercentage: 50 };
        }
        return pack;
      });
      setFinalPackage(finalUpdatedPacking);
    } else if (finalizedPackages.length === 1) {
      const finalUpdatedPacking = updatedPacking.map((pack) => {
        if (pack.isFinalPacking === 1) {
          return { ...pack, packingPercentage: 100 };
        }
        return pack;
      });
      setFinalPackage(finalUpdatedPacking);
    } else {
      setFinalPackage(updatedPacking);
    }
    setShowBottomSheet(false);
  };

  const handleAddMorePackaging = (): void => {
    const existingIndexes = finalPackage.map((_, i) => i);
    const availableIndex =
      [0, 1].find((index) => !existingIndexes.includes(index)) ?? 0;
    setpackageIndex(availableIndex);
    setShowBottomSheet(true);
  };

  const handleDeletePacking = (orderPackingId: number): void => {
    if (finalPackage.length != 0) {
      const remainingPacking = finalPackage.filter(
        (pack) => pack.orderPackingId !== orderPackingId,
      );
      if (remainingPacking.length === 0) {
        showToastMessage(errorMessage.ORDER_PACKAGE_COUNT_ERROR, 'error');
        return;
      }
      setFinalPackage(remainingPacking);
    }
  };

  const handlePercentageBlur = (value: number): void => {
    const intValue = Math.round(value);
    if (isNaN(intValue) || intValue <= 0 || intValue >= 100) {
      setPercentageError(true);
      showToastMessage(errorMessage.ORDER_PACKAGE_PERCENTAGE_INVALID, 'error');
    } else {
      setPercentageError(false);
    }
  };

  const handlePercentageChange = (
    packageId: number,
    rawValue: number | string,
  ): void => {
    // If the value is an empty string, treat as 0 (or allow clearing)
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      setFinalPackage((prev) =>
        prev.map((pack) =>
          pack.orderPackingId === packageId
            ? { ...pack, packingPercentage: 0 }
            : pack,
        ),
      );
      return;
    }

    // Ensure we have a number and strip decimals
    const numValue = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
    if (isNaN(numValue)) return;

    // Use Math.floor to remove any decimal part (so 52.5 becomes 52)
    let intValue = Math.floor(Math.abs(numValue));
    // Clamp between 0 and 100
    intValue = Math.max(0, Math.min(100, intValue));

    setFinalPackage((prevPacking) => {
      const updatedPacking = [...prevPacking];

      const packagingIndex = updatedPacking.findIndex(
        (pack) => pack.orderPackingId === packageId,
      );
      if (packagingIndex === -1) return prevPacking;

      updatedPacking[packagingIndex] = {
        ...updatedPacking[packagingIndex],
        packingPercentage: intValue,
        isFinalPacking: 1,
      };

      const finalPackages = updatedPacking.filter(
        (p) => p.isFinalPacking === 1,
      );

      if (finalPackages.length === 2) {
        const otherPackage = finalPackages.find(
          (p) => p.orderPackingId !== packageId,
        );
        if (otherPackage) {
          const otherIndex = updatedPacking.findIndex(
            (p) => p.orderPackingId === otherPackage.orderPackingId,
          );
          const otherValue = 100 - intValue;
          updatedPacking[otherIndex] = {
            ...updatedPacking[otherIndex],
            packingPercentage: Math.max(0, Math.min(100, otherValue)),
          };
        }
      }

      return updatedPacking;
    });
  };

  const handleQuantityChange = (type: 1 | -1): void => {
    setProductDetails((prevDetails) => {
      if (!prevDetails) return prevDetails;

      const updatedProducts = [...prevDetails.products];

      if (productIndex === -1) return prevDetails;

      const product = updatedProducts[productIndex];

      if (product.productType !== 2) return prevDetails;

      const currentQty =
        product.productFinalQuantity === 0
          ? product.productQuantity
          : product.productFinalQuantity;

      const newQty = type === 1 ? currentQty + 1 : Math.max(0, currentQty - 1);

      updatedProducts[productIndex] = {
        ...product,
        productFinalQuantity: newQty,
      };

      return {
        ...prevDetails,
        products: updatedProducts,
      };
    });
  };

  const handleWeightChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = event.target.value;
    if (value === '') {
      setFinalWeight('');
      return;
    }
    if (formRegex.DECIMAL_UP_TO_TWO_PLACES.test(value)) {
      setFinalWeight(value);
      return;
    }
  };

  const handleCompleteProcessing = async (): Promise<void> => {
    if (finalWeight == null || finalWeight === 0) {
      showToastMessage(errorMessage.PRODUCT_FINAL_WEIGHT_REQUIRED, 'error');
      return;
    }

    if (percentageError) {
      return;
    }

    const finalSelectedPackaging = finalPackage.filter(
      (p) => p.isFinalPacking === 1,
    );

    if (finalPackage.length > 1) {
      const hasZeroPercentage = finalPackage.some(
        (pack) => pack.packingPercentage === 0,
      );
      if (hasZeroPercentage) {
        showToastMessage(
          errorMessage.ORDER_PACKAGE_PERCENTAGE_ZERO_ERROR,
          'error',
        );
        return;
      }
    }

    const packagingWithAdjustedPercentage = finalSelectedPackaging.map(
      (pack) => {
        const matchedPackage = masterPackages.find(
          (pkg) =>
            pkg.packageType === pack.packageType &&
            pkg.packageSize === pack.packageSize,
        );

        return {
          productPackageTypeId:
            matchedPackage?.id ?? matchedPackage?.productPackageTypeLuId,
          percentage:
            finalSelectedPackaging.length === 1 ? 100 : pack.packingPercentage,
        };
      },
    );

    const totalPercentage = packagingWithAdjustedPercentage.reduce(
      (sum, p) => sum + (p.percentage ?? 0),
      0,
    );
    if (totalPercentage !== 100) {
      showToastMessage(
        errorMessage.ORDER_PACKAGE_PERCENTAGE_SPLIT_ERROR,
        'error',
      );
      return;
    }

    const product = productDetails.products[productIndex];

    const payload = {
      productId: product.orderProductId,
      status: 2,
      finalQuantity: Number(
        product.productType === ProductType.QUANTITY
          ? product.productFinalQuantity !== 0
            ? product.productFinalQuantity
            : product.productQuantity
          : finalWeight,
      ),
      packaging: packagingWithAdjustedPercentage,
    };
    try {
      await Axios.post(ApiEndpoints.PRODUCT_PROCESS_WORKFLOW, payload);
      showToastMessage(
        responseTexts.PRODUCT_WORKFLOW_FINISH_SUCCESS,
        'success',
      );
      navigate(FrontendRoutes.PRODUCT_PROCESSING_TASK_LIST, {
        state: { orderId: productDetails.orderId },
      });
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_PRODUCT_WORKFLOW, 'error');
      throw error;
    }
  };

  const handleCancel = (): void => {
    navigate(FrontendRoutes.PRODUCT_COMPLETED_DETAILS, {
      state: { speciesId, productName, orderNumber, productId, speciesName },
    });
  };

  const handleAddServiceNotes = (product: ProductProcessingProduct): void => {
    setActiveProductForNotes(product);
    setProductNotesModalMode('edit');
    setShowProductNotesModal(true);
    fetchproductDetails();
  };

  const BreadCrumbsProps = {
    steps: ProductProcessingCrumbSteps,
    activeItem: 'processItem_name',
    orderNumber: orderNumber,
    speciesName: speciesName,
    processItem_name: productName,
    type: OrderProcessingMasterData.PRODUCT_PROCESSING,
    stateProp: {
      speciesId,
      productName,
      orderNumber,
      editMode: true,
      productId,
      speciesName,
      orderId: productDetails.orderId,
    },
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      <SideNav active={5} />
      <div className="flex-1 p-1 overflow-auto relative">
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                {productName}
              </h2>
            </div>
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowNotesModal(true)}
                type="button"
                className="text-primary p-2 font-bold"
              >
                View Notes
              </button>
            </div>
            {showNotesModal && (
              <OrderNotesModal
                speciesName={productDetails?.speciesTag ?? ''}
                onClose={() => setShowNotesModal(false)}
                checkInNotes={
                  productDetails?.notes.orderCheckinNotes != ''
                    ? (productDetails?.notes.orderCheckinNotes ?? '')
                    : errorMessage.NO_NOTES_FOUND
                }
                AdditionalNotes={
                  productDetails?.notes.orderSpeciesNotes != ''
                    ? (productDetails?.notes.orderSpeciesNotes ?? '')
                    : errorMessage.NO_NOTES_FOUND
                }
                ServiceNotes={productDetails.notes.orderServiceNotes}
              />
            )}
          </div>
          <div className="mb-3">
            <ServiceTaskProgress currentStep="inProduct" type="product" />
          </div>
          {productDetails.products
            ?.filter((product) => product.orderProductId === productId)
            .map((product) => (
              <div
                key={product.orderProductId}
                className={`mt-3 p-3 rounded-lg shadow-md ${editMode ? 'bg-[#FFF9E6]' : 'bg-quaternary'}`}
              >
                <div>
                  <div className="flex justify-between items-center">
                    <h1 className="font-semibold text-xl mt-3">
                      {product.productName}
                      {product.fatPercent > 0 && `, ${product.fatPercent}% Fat`}
                    </h1>

                    <div className="flex gap-2">
                      <div className="flex justify-between gap-1">
                        {product.orderProductNotes && (
                          <div className="flex justify-between gap-1">
                            {product.orderProductNotes && (
                              <button
                                type="button"
                                className="text-primary font-bold ml-2"
                                onClick={() => handleAddServiceNotes(product)}
                              >
                                <FontAwesomeIcon
                                  className="mr-1"
                                  icon={faFilePen}
                                />
                                {''}
                                {product.orderProductNotes?.length
                                  ? 'Edit Notes'
                                  : 'Add Notes'}
                              </button>
                            )}
                            <span className="text-[#00000033] text-2xl">
                              |{' '}
                            </span>
                          </div>
                        )}
                        <OrderStatusBadge
                          status={product.productStatus}
                          type={'productStatus'}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F1F1F1] rounded-md shadow-md mt-5 p-3 border-[#00000033]">
                    <span className="font-semibold">Order Details</span>
                    <div className="flex items-center justify-between py-2 rounded-md">
                      <div>
                        <span className="text-[#43474E]">
                          {product.productType == ProductType.WEIGHT
                            ? 'Total Weight: '
                            : 'Total Quantity: '}
                        </span>
                        <span className="text-gray-800 font-semibold">
                          {product.productQuantity}{' '}
                          {product.productType == ProductType.WEIGHT
                            ? `${WeightType.LBS}`
                            : ''}
                        </span>
                      </div>
                      {product.productType == ProductType.QUANTITY && (
                        <div>
                          <span className="text-[#43474E] mt-2">
                            Total Estimated weight in Lbs:{' '}
                          </span>
                          <span className="text-gray-800 font-semibold">
                            <span className="text-gray-800 font-semibold">
                              {(
                                product.productQuantity * product.weight
                              ).toFixed(2)}{' '}
                              {WeightType.LBS}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>

                    {product.addons?.length > 0 && (
                      <div>
                        <span className="font-semibold mb-2">Add-Ons:</span>
                        <div className="flex flex-col gap-1 py-2 text-[#43474E]">
                          {product.addons.map((addon) => (
                            <span key={addon.addonId}>{addon.addonName}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.packing?.length > 0 && (
                      <div>
                        <span className="font-semibold mb-2">Packaging:</span>
                        <div className="space-y-2 py-2">
                          {product.packing
                            .filter((pack) => pack.isFinalPacking === 0)
                            .map((pack) => {
                              return (
                                <div
                                  key={pack.orderPackingId}
                                  className="flex justify-between"
                                >
                                  <span className="w-1/4 text-[#43474E]">
                                    {pack?.packageType}
                                  </span>
                                  <span className="w-1/4 text-center">
                                    <span className="text-[#43474E]">
                                      Size:{' '}
                                    </span>
                                    {pack?.packageSize}{' '}
                                    <span className="text-black">
                                      {WeightType.LBS}
                                    </span>
                                  </span>
                                  {product.packing.length > 1 && (
                                    <span className="w-1/4 text-center">
                                      <span className="text-[#43474E]">
                                        Pkg Split:
                                      </span>{' '}
                                      <span className="text-black">
                                        {pack.packingPercentage}%
                                      </span>
                                    </span>
                                  )}
                                  <span className="w-1/4 text-right">
                                    <span className="text-[#43474E]">
                                      Price:{' '}
                                    </span>
                                    <span className="text-black">
                                      ${pack.packingPrice}
                                    </span>
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                    {product.orderPackingNotes && (
                      <div className="mt-3">
                        <h4 className="font-medium mb-2">Packaging Notes:</h4>
                        <span className="text-[#43474E]">
                          {product.orderPackingNotes}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="bg-[#FFFFFF] rounded-md shadow-md mt-5 mb-3 p-3">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center w-full gap-3">
                        {product.productType === ProductType.QUANTITY ? (
                          <>
                            <div className="flex justify-between items-center w-full">
                              <h1 className="text-lg text-gray-800 font-medium">
                                Final Quantity after Processing
                              </h1>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <button
                                    onClick={() => handleQuantityChange(-1)}
                                    type="button"
                                    className="bg-primary text-white font-bold py-1 px-4 rounded-l"
                                  >
                                    -
                                  </button>
                                  <span className="px-4 w-24 py-1 text-center border border-gray-200">
                                    <span>
                                      {product.productFinalQuantity !== 0
                                        ? product.productFinalQuantity
                                        : product.productQuantity}
                                    </span>
                                  </span>
                                  <button
                                    onClick={() => handleQuantityChange(1)}
                                    type="button"
                                    className="bg-primary text-white font-bold py-1 px-4 rounded-r"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between items-center w-full">
                              <h1 className="text-lg text-gray-800 font-medium">
                                Final Weight after Processing
                              </h1>
                              <div className="flex items-center gap-2">
                                <label className="font-semibold text-[#1C1B1F]">
                                  Weight:
                                </label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  name="weight"
                                  value={finalWeight ?? ''}
                                  onChange={(event) =>
                                    handleWeightChange(event)
                                  }
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                                <span>{WeightType.LBS}</span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <h3 className="mb-2 text-lg text-gray-800 font-medium">
                      Choose final Packaging
                    </h3>
                    {(product.productStatus === 2
                      ? finalPackage.filter((pack) => pack.isFinalPacking === 1)
                      : finalPackage
                    ).map((products, index) => (
                      <div
                        key={index}
                        className="flex items-center mb-2 justify-between bg-white"
                      >
                        <div
                          className="flex-1 relative"
                          onClick={() => {
                            console.log(finalPackage);
                            setShowBottomSheet(true);
                            setpackageIndex(index);
                          }}
                        >
                          <div className="flex items-center border border-gray-300 rounded px-3 py-1 h-10 text-sm cursor-pointer">
                            <div className="flex items-center w-full justify-between">
                              <span className="text-gray-800 truncate min-w-[80px] flex-shrink-0">
                                {products.packageType}
                              </span>
                              <span className="text-gray-600 text-center min-w-[80px] flex-shrink-0">
                                {products.packageSize} {WeightType.LBS}
                              </span>
                              <span className="font-semibold text-right min-w-[100px] flex-shrink-0">
                                + ${products.packingPrice} /{WeightType.LBS}
                              </span>
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className="text-[#A09CAB] text-sm"
                              />
                            </div>
                          </div>
                        </div>
                        {(product.productStatus === 2
                          ? finalPackage.filter(
                            (pack) => pack.isFinalPacking === 1,
                          ).length > 1
                          : finalPackage.length > 1) && (
                            <div className="relative w-[80px] ml-2">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={products.packingPercentage}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  // Remove any non-digit characters
                                  const digits = raw.replace(/\D/g, '');
                                  if (digits === '') {
                                    handlePercentageChange(
                                      products.orderPackingId,
                                      0,
                                    );
                                    return;
                                  }
                                  const num = parseInt(digits, 10);
                                  handlePercentageChange(
                                    products.orderPackingId,
                                    num,
                                  );
                                }}
                                onBlur={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  if (!isNaN(val)) {
                                    handlePercentageBlur(val);
                                  }
                                }}
                                className={`h-10 w-full pr-5 pl-2 border rounded-md focus:outline-primary text-center ${percentageError
                                  ? 'border-red-500'
                                  : 'border-gray-300'
                                  }`}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                %
                              </span>
                            </div>
                          )}
                        <button
                          type="button"
                          onClick={() =>
                            handleDeletePacking(products.orderPackingId)
                          }
                          className="mr-2 ml-4 py-1.5 px-2 rounded border text-gray-800"
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </div>
                    ))}
                    {finalPackage.filter((pack) => pack.isFinalPacking === 1)
                      .length < 2 && (
                        <button
                          type="button"
                          onClick={handleAddMorePackaging}
                          className="w-full text-primary border bg-white border-primary font-medium py-1 rounded mb-4 mt-2"
                        >
                          Add More Packaging
                        </button>
                      )}
                  </div>

                  {product.productProcessDetail && (
                    <div className="p-1 mt-3 flex justify-between">
                      {product.productProcessDetail.productStartDate && (
                        <span className="text-gray-600">
                          Start date:{' '}
                          {new Date(
                            product.productProcessDetail.productStartDate,
                          ).toLocaleDateString('en-US')}
                        </span>
                      )}
                      {product.productProcessDetail.productEndDate && (
                        <span className="text-gray-600">
                          End date:{' '}
                          {new Date(
                            product.productProcessDetail.productEndDate,
                          ).toLocaleDateString('en-US')}
                        </span>
                      )}
                    </div>
                  )}
                  {editMode ? (
                    <div className="flex justify-between gap-4 mt-3">
                      <button
                        onClick={handleCancel}
                        type="button"
                        className="w-[50%] px-5 py-2 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        onClick={handleCompleteProcessing}
                        className="w-[50%] px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                      >
                        <span>Save Changes</span>
                      </button>
                    </div>
                  ) : (
                    <div>
                      <button
                        onClick={handleCompleteProcessing}
                        type="button"
                        className="w-full mt-3 px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                      >
                        Complete Product Processing & Packaging
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          {showBottomSheet && (
            <PackagingBottomSheet
              onClose={() => {
                setShowBottomSheet(false);
              }}
              packageIndex={packageIndex}
              onSelect={handleSelectPackaging}
            />
          )}
          <ProductNotesModal
            isOpen={showProductNotesModal}
            onClose={() => {
              setShowProductNotesModal(false);
              setActiveProductForNotes(null);
              fetchproductDetails();
            }}
            productNotes={activeProductForNotes?.orderProductNotes || []}
            orderId={productDetails?.orderId ?? 0}
            orderSpeciesId={productDetails?.orderSpeciesId ?? 0}
            orderProductId={activeProductForNotes?.orderProductId ?? 0}
            showToastMessage={showToastMessage}
            mode={productNotesModalMode}
          />
        </div>
      </div>
    </div>
  );
};